// src/pages/libros/Libros.js
import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, TextField, CircularProgress, Alert,
  Paper, Grid, styled
} from '@mui/material';
import { FileDownloadOutlined as DownloadIcon } from '@mui/icons-material';

import { ActionButton, SecondaryButton } from '../../components/common/ActionButton';
import { librosApi } from '../../services/api';
import LibroTabla, { renglonACelda, CABECERA_CSV } from './LibroTabla';

const PageContainer = styled(Box)(() => ({
  padding: '24px',
  backgroundColor: '#0A0A0C',
  color: 'white',
  minHeight: 'calc(100vh - 64px)'
}));

const bs = (n) => (Number(n) || 0).toLocaleString('es-VE', {
  minimumFractionDigits: 2, maximumFractionDigits: 2
});

const mesActual = () => {
  const hoy = new Date();
  const anio = hoy.getFullYear();
  const mes = String(hoy.getMonth() + 1).padStart(2, '0');
  const ultimo = new Date(anio, hoy.getMonth() + 1, 0).getDate();
  return { desde: `${anio}-${mes}-01`, hasta: `${anio}-${mes}-${ultimo}` };
};

const estiloFecha = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&.Mui-focused fieldset': { borderColor: '#F5A623' }
  }
};

/** Tarjeta de un número grande con su explicación. */
const Cifra = ({ titulo, monto, detalle, destacada }) => (
  <Paper sx={{
    p: 2.5, bgcolor: destacada ? 'rgba(245,166,35,0.1)' : '#131316',
    backgroundImage: 'none',
    border: destacada ? '1px solid rgba(245,166,35,0.35)' : '1px solid rgba(255,255,255,0.08)',
    height: '100%'
  }}>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>{titulo}</Typography>
    <Typography variant="h5" sx={{
      fontWeight: 700, color: destacada ? '#F5A623' : 'white',
      fontVariantNumeric: 'tabular-nums'
    }}>
      Bs. {bs(monto)}
    </Typography>
    {detalle && (
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>{detalle}</Typography>
    )}
  </Paper>
);

/**
 * Libros de Ventas y de Compras.
 *
 * Son los dos registros que exige el Reglamento de la Ley del IVA y lo primero
 * que pide una fiscalización. No se cargan: se arman de lo que ya está
 * registrado —las facturas emitidas, las compras cargadas— así que esta pantalla
 * es de sólo lectura.
 *
 * El resumen es lo que en la práctica se busca acá: cuánto débito fiscal generó
 * el mes, cuánto crédito lo descuenta, y qué queda.
 */
export const Libros = () => {
  const [pestania, setPestania] = useState(0);
  // Un libro fiscal se presenta completo, pero dibujar miles de filas mata la
  // pantalla. Se pide de a páginas; los totales que devuelve el servidor son
  // siempre del período entero, no de la página.
  const [pagina, setPagina] = useState(1);
  const POR_PAGINA = 50;
  const [periodo, setPeriodo] = useState(mesActual);
  const [datos, setDatos] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const esVentas = pestania === 0;

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [libro, res] = await Promise.all([
        esVentas
          ? librosApi.ventas({ ...periodo, pagina, porPagina: POR_PAGINA })
          : librosApi.compras({ ...periodo, pagina, porPagina: POR_PAGINA }),
        librosApi.resumen(periodo)
      ]);
      setDatos(libro);
      setResumen(res);
    } catch (e) {
      setError(e.message || 'No se pudo armar el libro.');
    } finally {
      setCargando(false);
    }
  }, [esVentas, periodo, pagina]);

  useEffect(() => { cargar(); }, [cargar]);

  /**
   * Exporta a CSV para abrir en Excel en español.
   *
   * Dos decisiones van juntas: el separador de columnas es punto y coma y el
   * decimal es coma. Excel en español lee la coma como decimal, así que un CSV
   * separado por comas le desarma las columnas; y con el punto decimal leería
   * 180948.91 como dieciocho millones. Las dos cosas tienen que cambiar a la vez.
   */
  const exportar = async () => {
    if (!datos?.renglones?.length) return;
    // El CSV lleva el libro completo, no la página que se está mirando: es el
    // archivo que va al contador.
    const completo = esVentas ? await librosApi.ventas(periodo) : await librosApi.compras(periodo);
    const filas = [
      CABECERA_CSV(esVentas),
      ...completo.renglones.map((r) => renglonACelda(r, esVentas))
    ];
    const celdaACsv = (celda) => {
      if (typeof celda === 'number') return celda.toFixed(2).replace('.', ',');
      const texto = String(celda ?? '');
      return /[";\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
    };
    const csv = filas
      .map((fila) => fila.map(celdaACsv).join(';'))
      .join('\n');

    // El BOM le dice a Excel que el archivo es UTF-8; sin él se comen los acentos.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = `libro-${esVentas ? 'ventas' : 'compras'}-${periodo.desde}-a-${periodo.hasta}.csv`;
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    URL.revokeObjectURL(url);
  };

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Libros fiscales</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {datos?.empresa
              ? `${datos.empresa.nombre} — RIF ${datos.empresa.rif}`
              : 'Se arman de lo ya registrado'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
            value={periodo.desde} onChange={(e) => { setPagina(1); setPeriodo((p) => ({ ...p, desde: e.target.value })); }}
            sx={estiloFecha} />
          <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
            value={periodo.hasta} onChange={(e) => { setPagina(1); setPeriodo((p) => ({ ...p, hasta: e.target.value })); }}
            sx={estiloFecha} />
          <ActionButton startIcon={<DownloadIcon />} onClick={exportar}
            disabled={cargando || !datos?.renglones?.length}>
            Exportar
          </ActionButton>
        </Box>
      </Box>

      {resumen && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Cifra titulo="Débito fiscal (ventas)" monto={resumen.debitoFiscal}
              detalle={`${resumen.ventas.cantidad} documento${resumen.ventas.cantidad === 1 ? '' : 's'}`} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Cifra titulo="Crédito fiscal (compras)" monto={resumen.creditoFiscal}
              detalle={`${resumen.compras.cantidad} documento${resumen.compras.cantidad === 1 ? '' : 's'}`} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Cifra
              titulo={resumen.aPagar > 0 ? 'A pagar al fisco' : 'Excedente al mes siguiente'}
              monto={resumen.aPagar > 0 ? resumen.aPagar : resumen.excedenteASiguienteMes}
              detalle={resumen.aPagar > 0 ? 'Débito menos crédito' : 'El crédito superó al débito'}
              destacada
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Cifra titulo="Ventas del período" monto={resumen.ventas.totalConIva} detalle="Total con IVA" />
          </Grid>
        </Grid>
      )}

      {resumen?.advertencias?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {resumen.advertencias.map((a, i) => <div key={i}>{a}</div>)}
        </Alert>
      )}

      <Tabs
        value={pestania}
        onChange={(e, v) => { setPestania(v); setPagina(1); }}
        sx={{
          mb: 2,
          '& .MuiTab-root': { color: 'rgba(255,255,255,0.6)', textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: '#F5A623 !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#F5A623' }
        }}
      >
        <Tab label="Libro de Ventas" />
        <Tab label="Libro de Compras" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#F5A623' }} />
        </Box>
      ) : datos ? (
        <LibroTabla
          renglones={datos.renglones}
          totales={datos.totales}
          tipo={esVentas ? 'ventas' : 'compras'}
        />
      ) : null}

      {datos?.paginacion && datos.paginacion.paginas > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {datos.cantidad} renglones en el período · mostrando {datos.paginacion.mostrando}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecondaryButton onClick={() => setPagina((p) => Math.max(1, p - 1))}
              disabled={pagina <= 1 || cargando}>Anterior</SecondaryButton>
            <Typography variant="body2" sx={{ color: 'white', minWidth: 90, textAlign: 'center' }}>
              Página {datos.paginacion.pagina} de {datos.paginacion.paginas}
            </Typography>
            <SecondaryButton onClick={() => setPagina((p) => p + 1)}
              disabled={pagina >= datos.paginacion.paginas || cargando}>Siguiente</SecondaryButton>
          </Box>
        </Box>
      )}

      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 2 }}>
        Los totales de arriba son del período completo, no de la página que estás viendo.
        Los montos van en bolívares. Una factura emitida en divisa se convierte con
        la tasa del día en que se emitió, no con la de hoy.
      </Typography>
    </PageContainer>
  );
};

export default Libros;
